import querystringify from "querystringify";
import * as React from "react";
import { UserService } from "../../services/user-service";
import { IWithHistoryProp, withHistory } from "../../shared/hoc/with-history";
import { KMainFunctions } from "../../shared/utility/main-functions";
import AuthWorkspaceJoinedSvg from "../../svgs/auth-workspace-joined.svg";
import KuikaButton from "../ui-kit/kuika-button/kuika-button";
import KuikaSpinner from "../ui-kit/kuika-spinner/kuika-spinner";
import KuikaH1 from "../ui-kit/typography/H1/H1";
import KuikaH4 from "../ui-kit/typography/H4/H4";
import KuikaText from "../ui-kit/typography/Text/Text";
import { AuthLogo } from "./auth-logo";
import { RegistrationForm } from "./registration-form";

interface IInvitationState {
  username?: string;
  domainName?: string;
  isAccept: boolean;
  isLoading: boolean;
  isRegister: boolean;
  countdown: number;
}

interface IInvitationProps {}

interface IPageQueries extends RespondInvitationDTO {
  accepted: "0" | "1";
}

export interface RespondInvitationDTO {
  username: string;
  domainName: string;
}

export interface InvitationResponseDTO {
  invitationResult: 0 | 1 | 2 | 3;
}

class Invitation extends React.PureComponent<IInvitationProps & IWithHistoryProp, IInvitationState> {
  timer: NodeJS.Timeout | null = null;

  constructor(props: IInvitationProps & IWithHistoryProp) {
    super(props);
    this.state = {
      isAccept: false,
      isLoading: true,
      isRegister: false,
      countdown: 5
    };
  }

  async componentDidMount() {
    const querySearch = this.props?.navigator?.location?.search;
    const queryParams = (querySearch ? querystringify.parse(querySearch) : {}) as IPageQueries;
    const { accepted, username, domainName } = queryParams;

    // Opened without a valid invitation link (e.g. navigating straight to /invitation).
    // A react-router navigate() here would be dropped: componentDidMount runs before the
    // router's own mount effect is active ("call navigate() in a useEffect" warning), leaving
    // the user stuck on the spinner. A hard redirect reliably lands them on the login page
    // (which is a separate build entry anyway) and keeps the bad URL out of history.
    if (!username || !domainName || !accepted) {
      window.location.replace("/login");
      return;
    }

    try {
      switch (accepted) {
        case "0":
          await this.handleRejectInvite({ username, domainName });
          break;
        case "1":
          await this.handleAcceptInvite({ username, domainName });
          break;
        default:
          window.location.replace("/login");
          return;
      }
    } catch (error) {
      // Reached after an await, so the router is active here and SPA navigation works
      // (and preserves the error toast shown by exceptionHandler).
      KMainFunctions.exceptionHandler(error);
      this.props.navigator.gotoLogin();
    }
  }

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  startCountdown = (onComplete: () => void) => {
    this.timer = setInterval(() => {
      this.setState(
        (prevState) => ({ countdown: prevState.countdown - 1 }),
        () => {
          if (this.state.countdown === 0) {
            if (this.timer) clearInterval(this.timer);
            onComplete();
          }
        }
      );
    }, 1000);
  };

  async handleAcceptInvite({ username, domainName }: RespondInvitationDTO) {
    const res = await UserService.AcceptInvite({ username, domainName });

    if (res.status !== 200) {
      throw Object.assign(new Error(), res);
    }

    const {
      data: { invitationResult }
    } = res;

    switch (invitationResult) {
      case 0:
        // KOA-17793: brand-new invited user completes registration inline here
        // (the standalone /signup screen was removed) instead of being redirected.
        this.setState({ isLoading: false, isRegister: true, username, domainName });
        break;
      case 1:
        this.setState({ isLoading: false, isAccept: true, username, domainName, countdown: 5 }, () => {
          this.startCountdown(() => {
            this.props.navigator.goToExactDomain(domainName, true);
          });
        });
        break;
      case 3:
        throw new Error("Invitation is expired or not found.");
      default:
        throw new Error();
    }
  }

  async handleRejectInvite({ username, domainName }: RespondInvitationDTO) {
    const res = await UserService.RejectInvite({ username, domainName });

    if (res.status !== 200) {
      throw Object.assign(new Error(), res);
    }

    const {
      data: { invitationResult }
    } = res;

    switch (invitationResult) {
      case 2:
        this.setState({ isLoading: false, isAccept: false, username, domainName, countdown: 5 }, () => {
          this.startCountdown(() => {
            this.props.navigator.gotoRoot();
          });
        });
        break;
      case 3:
        throw new Error("Invitation is expired or not found.");
      default:
        throw new Error();
    }
  }

  renderLoading() {
    return (
      <div>
        <KuikaSpinner spinning={true} size="large" />
      </div>
    );
  }

  render() {
    if (this.state.isLoading) return this.renderLoading();

    if (this.state.isRegister) {
      return (
        <RegistrationForm
          username={this.state.username ?? ""}
          domainName={this.state.domainName}
          onSuccess={() => this.props.navigator.goToEmailConfirmation()}
        />
      );
    }

    const isMainAI = KMainFunctions.isMainAI();

    return (
      <div className="email-confirmation">
        <div className="email-confirmation__content">
          <div className="email-confirmation__top-section">
            <AuthLogo />

            <div className="email-confirmation__check-inbox-card">
              <div className="email-confirmation__title-row">
                <AuthWorkspaceJoinedSvg />
                <KuikaH4>{this.state.isAccept ? "Workspace joined" : "Workspace rejected"}</KuikaH4>
              </div>

              <div className="email-confirmation__line" />

              <div className="email-confirmation__countdown-wrapper">
                <KuikaH1 style={{ color: "var(--dark-8)", margin: 0 }}>{this.state.countdown}</KuikaH1>
              </div>

              <KuikaText className="email-confirmation__message-text">
                {this.state.isAccept ? (
                  <>
                    You have accepted the <strong>{this.state.domainName}</strong> workspace invitation. You can switch
                    to other workspaces from the Apps screen. You are being redirected to your account.
                  </>
                ) : (
                  <>
                    You have declined the <strong>{this.state.domainName}</strong> workspace invitation.
                  </>
                )}
              </KuikaText>
            </div>
          </div>

          {!isMainAI && (
            <div className="email-confirmation__bottom-section">
              <KuikaButton
                type="text"
                size="sm"
                onClick={() => window.open("https://www.kuika.com/en/legal/privacy", "_blank")}
                uppercase
              >
                Privacy
              </KuikaButton>
              <KuikaButton
                type="text"
                size="sm"
                onClick={() => window.open("https://www.kuika.com/en/legal/terms", "_blank")}
                uppercase
              >
                Terms
              </KuikaButton>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const invitation = withHistory<IInvitationProps>(Invitation);
export { invitation as Invitation };
